import { EarningsItemBase, FinancialData } from '@api';
import { SmartTableColumn, WithNAFallback } from '@design';
import { formatPercentage, getCurrencySymbol } from '@helpers';
import { formatNumberWithUnit } from '@terminal/widgets/earnings/utils/utils';
import { SharedTable } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/shared-table/SharedTable';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './EarningsHighlightsTable.module.scss';

export interface EarningsHighlightsTableProps {
  earningsData: EarningsItemBase;
}

export const EarningsHighlightsTable = ({
  earningsData,
}: EarningsHighlightsTableProps) => {
  const { t } = useTranslation(['earnings']);
  const { eps, revenue } = earningsData;

  const naContainerPadding = '10px 12px';

  const tableData = [
    {
      name: t('earnings:earningsInsight.drawer.earningsRecap.tables.eps'),
      key: 'eps',
      ...eps,
    },
    {
      name: t('earnings:earningsInsight.drawer.earningsRecap.tables.revenue'),
      key: 'revenue',
      ...revenue,
    },
  ];

  const tableColumns: SmartTableColumn<
    FinancialData & { name: string; key: string }
  >[] = [
    {
      label: 'Name',
      key: 'name',
      renderHeader: () => null,
      render: value => <div className={styles.styledNameCell}>{value}</div>,
    },
    {
      label: t('earnings:earningsInsight.drawer.earningsRecap.tables.estimate'),
      key: 'estimate',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(earningsData.currency);
        return (
          <WithNAFallback
            containerPadding={naContainerPadding}
            value={
              value !== undefined && (
                <div className={styles.numberCell}>
                  {`${currencySymbol}${formatNumberWithUnit(value)}`}
                </div>
              )
            }
          />
        );
      },
    },
    {
      label: t('earnings:earningsInsight.drawer.earningsRecap.tables.reported'),
      key: 'value',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(earningsData.currency);
        return (
          <WithNAFallback
            containerPadding={naContainerPadding}
            value={
              value !== undefined && (
                <div className={styles.numberCell}>
                  {`${currencySymbol}${formatNumberWithUnit(value)}`}
                </div>
              )
            }
          />
        );
      },
    },
    {
      label: t('earnings:earningsInsight.drawer.earningsRecap.tables.surprise'),
      key: 'surprise',
      alignment: 'right',
      render: value => {
        if (value === undefined) {
          return (
            <WithNAFallback
              containerPadding={naContainerPadding}
              value={undefined}
            />
          );
        }

        const currencySymbol = getCurrencySymbol(earningsData.currency);
        const formatted = formatNumberWithUnit(value);
        const displayValue = formatted.startsWith('-')
          ? `-${currencySymbol}${formatted.slice(1)}`
          : `${currencySymbol}${formatted}`;

        return (
          <WithNAFallback
            containerPadding={naContainerPadding}
            value={
              <div
                className={clsx(styles.surpriseCell, {
                  [styles.surpriseCellPositive]: value > 0,
                  [styles.surpriseCellZero]: value === 0,
                })}
              >
                {displayValue}
              </div>
            }
          />
        );
      },
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsRecap.tables.surprisePercentage'
      ),
      key: 'surprise_percentage',
      alignment: 'right',
      render: value => (
        <WithNAFallback
          containerPadding={naContainerPadding}
          value={
            value !== undefined && (
              <div
                className={clsx(styles.surpriseCell, {
                  [styles.surpriseCellPositive]: value > 0,
                  [styles.surpriseCellZero]: value === 0,
                })}
              >
                {formatPercentage(value, {
                  suffix: '%',
                  addPlus: true,
                  useOriginalValue: true,
                })}
              </div>
            )
          }
        />
      ),
    },
  ];
  return (
    <SharedTable
      title={t(
        'earnings:earningsInsight.drawer.earningsRecap.earningsHighlights'
      )}
      tableData={tableData}
      tableColumns={tableColumns}
      noCellPadding
    />
  );
};
